import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const COMMON = [
  "Nightly live human monitoring from Guwahati command center (10 PM – 6 AM, or as per client requirement)",
  "Instant call + Activate Alarm & local police",
  "Daily activity report on WhatsApp",
  "Free connection to your existing DVR / NVR",
  "Local Assam-based support team",
  "No hidden charges",
];

const PLANS = [
  {
    id: "starter",
    name: "Starter Watch",
    tag: "Best for homes & small shops",
    cams: "2 Cameras",
    price: "1,499",
    perDay: "50",
    period: "/ month",
    highlight: false,
    features: [
      "2 cameras monitored 10 P.M – 6 A.M by trained live operators (or as per client requirement)",
      ...COMMON,
    ],
    cta: "Start with Starter",
  },
  {
    id: "shield",
    name: "Shield Watch",
    tag: "Popular for shops & offices",
    cams: "4 Cameras",
    price: "4,999",
    perDay: "167",
    period: "/ month",
    highlight: true,
    features: [
      "4 cameras monitored 10 P.M – 6 A.M by trained live operators (or as per client requirement)",
      ...COMMON,
      "Priority response · call within 20 minutes",
    ],
    cta: "Book Shield plan",
  },
  {
    id: "fortress",
    name: "Fortress Watch",
    tag: "Warehouses, godowns & showrooms",
    cams: "8 Cameras",
    price: "8,999",
    perDay: "300",
    period: "/ month",
    highlight: false,
    features: [
      "8 cameras monitored 10 P.M – 6 A.M by trained live operators (or as per client requirement)",
      ...COMMON,
      "Priority response · call within 15 minutes",
      "Dedicated operator during your monitoring window",
    ],
    cta: "Book Fortress plan",
  },
  {
    id: "custom",
    name: "Enterprise Watch",
    tag: "16+ cameras · Custom SLA",
    cams: "16+ Cameras",
    price: "Custom",
    period: "",
    highlight: false,
    features: [
      "Unlimited cameras across multiple sites",
      ...COMMON,
      "Dedicated command-center pod for your business",
      "Custom SLAs, incident escalation & monthly review",
    ],
    cta: "Talk to sales",
  },
];

const MONITOR_IMAGES = [
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_cctv-leads/artifacts/l6e3ykz9_Guwahati%20Command%20Centre%20Guw.jpeg",
    caption: "Guwahati Command Center · Live human operators",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_cctv-leads/artifacts/g3gyo2y5_alert%20image.jpeg",
    caption: "Trained watchers · Zero missed alerts",
  },
  {
    src: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1200&q=80&auto=format&fit=crop",
    caption: "Nightly verification · No false alarms",
  },
];

export default function Plans() {
  return (
    <section id="plans" className="relative border-t border-white/10 bg-[#0B0B0E] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div {...fadeUp} className="max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-cyan">
            Plans & Pricing
          </span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">
            Real people watching. <span className="text-brand-cyan text-glow">Real prices.</span>
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl">
            No bots. No AI guessing games. Every camera you sign up is watched live by our trained
            operators from our Guwahati command center — every night, 10 PM to 6 AM.
          </p>
        </motion.div>

        {/* Live human monitoring photo strip */}
        <motion.div {...fadeUp} className="mt-10 grid gap-3 sm:grid-cols-3">
          {MONITOR_IMAGES.map((img, i) => (
            <div
              key={i}
              data-testid={`monitor-image-${i}`}
              className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3] group"
            >
              <img
                src={img.src}
                alt={img.caption}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-black/60 border border-brand-cyan/40 px-2.5 py-1 backdrop-blur-md">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan pulse-dot" />
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-brand-cyan">Live</span>
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-white text-sm font-semibold">{img.caption}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Plans grid */}
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-2xl border p-6 flex flex-col ${
                p.highlight
                  ? "border-brand-cyan bg-gradient-to-b from-brand-cyan/15 to-transparent shadow-[0_0_40px_rgba(0,229,255,0.15)]"
                  : "border-white/10 bg-[#0F0F13] hover:border-white/25 transition-colors"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 rounded-full bg-brand-cyan text-black text-[10px] font-bold uppercase tracking-[0.2em]">
                  <Star className="h-3 w-3 fill-black" />
                  Most Popular
                </div>
              )}

              <div className="flex items-baseline gap-2">
                <h3 className="font-heading text-xl font-bold text-white">{p.name}</h3>
              </div>
              <p className="text-xs text-white/50 mt-1">{p.tag}</p>

              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-1 self-start">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" />
                <span className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-semibold">
                  {p.cams}
                </span>
              </div>

              {p.price === "Custom" ? (
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-extrabold text-white">Custom</span>
                </div>
              ) : (
                <>
                  <div
                    className={`mt-5 relative overflow-hidden rounded-xl border px-3 py-3 ${
                      p.highlight
                        ? "border-brand-cyan/60 bg-brand-cyan/10"
                        : "border-brand-cyan/30 bg-brand-cyan/[0.06]"
                    }`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-cyan/90">
                      Just from
                    </div>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className="text-xl font-semibold text-brand-cyan">₹</span>
                      <span className="font-heading text-5xl sm:text-6xl font-extrabold text-brand-cyan leading-none text-glow">
                        {p.perDay}
                      </span>
                      <span className="text-base font-bold text-brand-cyan/90 ml-1">/ day</span>
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-1.5">
                      <span className="text-[11px] uppercase tracking-wider text-white/50">Billed monthly</span>
                      <span className="text-sm text-white/80 font-semibold">₹{p.price}</span>
                      <span className="text-[11px] text-white/50">{p.period}</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-white/40 mt-2">+ one-time setup</p>
                </>
              )}

              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/75">
                    <span
                      className={`mt-0.5 h-4 w-4 rounded-full flex items-center justify-center shrink-0 ${
                        p.highlight ? "bg-brand-cyan text-black" : "bg-brand-cyan/15 text-brand-cyan"
                      }`}
                    >
                      <Check className="h-2.5 w-2.5" />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact" data-testid={`plan-cta-${p.id}`} className="mt-6">
                <Button
                  className={`w-full h-11 font-semibold ${
                    p.highlight
                      ? "bg-brand-cyan text-black hover:bg-brand-cyan/90 shadow-[0_0_25px_rgba(0,229,255,0.4)]"
                      : "bg-white/10 text-white border border-white/15 hover:bg-white/15"
                  }`}
                >
                  {p.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeUp} className="mt-10 rounded-2xl border border-white/10 bg-[#0F0F13] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-brand-cyan/15 border border-brand-cyan/30 text-brand-cyan flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-white font-semibold">Not sure which plan fits you?</p>
              <p className="text-white/60 text-sm mt-1">
                Book a free on-site audit — we'll count your cameras and suggest the smartest plan.
              </p>
            </div>
          </div>
          <Link to="/contact" data-testid="plans-audit-cta">
            <Button className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold shadow-[0_0_20px_rgba(0,229,255,0.3)]">
              Book free audit
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
